/**
 * Files — `docs/API.md` §11's presigned upload/download pair, plus listing
 * and detaching a record's attachments (`salescrmapi/src/modules/files`).
 *
 * Bytes never pass through `apiClient`: `uploadToPresignedUrl` PUTs straight
 * to the MinIO URL `createUploadUrl` returns, with a bare `axios.put` rather
 * than the app's instance — that instance adds an `Authorization` bearer
 * token and a JSON `Content-Type`, neither of which belongs on a request to
 * MinIO, whose own presigned signature is the credential.
 */
import axios from 'axios';
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {{filename:string, content_type:string, byte_size:number, checksum_sha256:string, purpose?:string, entity_key?:string, record_id?:string}} body */
export function createUploadUrl(body) {
  return withMockFallback(
    'POST /files/upload-url',
    async () => (await apiClient.post('/files/upload-url', body)).data,
    () => { throw new Error('Document upload is not available in this build.'); },
  );
}

/** PUT the file's bytes straight to MinIO. Not routed through apiClient — see this module's doc comment. */
export function uploadToPresignedUrl(upload, file) {
  return axios.put(upload.url, file, { headers: upload.headers });
}

/** @param {string} objectId @param {{etag:string, purpose?:string, entity_key?:string, record_id?:string}} body */
export function commitUpload(objectId, body) {
  return withMockFallback(
    `POST /files/${objectId}/commit`,
    async () => (await apiClient.post(`/files/${objectId}/commit`, body)).data,
    () => { throw new Error('Document upload is not available in this build.'); },
  );
}

/** @param {string} objectId @param {{disposition?:string, expires_in?:number}} [params] */
export function createDownloadUrl(objectId, params = {}) {
  return withMockFallback(
    `GET /files/${objectId}/download-url`,
    async () => (await apiClient.get(`/files/${objectId}/download-url`, { params })).data,
    () => { throw new Error('Document download is not available in this build.'); },
  );
}

/** @param {{entity_key:string, record_id:string, limit?:number, cursor?:string}} params */
export function listAttachments(params) {
  return withMockFallback(
    'GET /files',
    async () => (await apiClient.get('/files', { params })).data,
    () => ({ data: [], page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false } }),
  );
}

export function deleteAttachment(attachmentId) {
  return withMockFallback(
    `DELETE /files/attachments/${attachmentId}`,
    async () => { await apiClient.delete(`/files/attachments/${attachmentId}`); },
    () => { throw new Error('Removing documents is not available in this build.'); },
  );
}
